import { useCallback, useEffect, useState } from "react";

interface MobileNavigationOptions {
  preventBodyScroll?: boolean;
  enableSwipeGestures?: boolean;
  closeOnOutsideClick?: boolean;
}

interface MobileNavigationState {
  isOpen: boolean;
  isAnimating: boolean;
  touchStartX: number | null;
  touchStartY: number | null;
}

export function useMobileNavigation(options: MobileNavigationOptions = {}) {
  const {
    preventBodyScroll = true,
    enableSwipeGestures = true,
    closeOnOutsideClick = true,
  } = options;

  const [state, setState] = useState<MobileNavigationState>({
    isOpen: false,
    isAnimating: false,
    touchStartX: null,
    touchStartY: null,
  });

  // Open navigation
  const openNavigation = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true, isAnimating: true }));

    if (preventBodyScroll) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    }

    // Add accessibility
    const focusableElements = document.querySelectorAll(
      '[data-navigation-content] button, [data-navigation-content] a, [data-navigation-content] input, [data-navigation-content] select, [data-navigation-content] textarea, [data-navigation-content] [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length > 0) {
      (focusableElements[0] as HTMLElement).focus();
    }

    // Animation complete
    setTimeout(() => {
      setState((prev) => ({ ...prev, isAnimating: false }));
    }, 300);
  }, [preventBodyScroll]);

  // Close navigation
  const closeNavigation = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false, isAnimating: true }));

    if (preventBodyScroll) {
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("position");
      document.body.style.removeProperty("width");
    }

    // Animation complete
    setTimeout(() => {
      setState((prev) => ({ ...prev, isAnimating: false }));
    }, 300);
  }, [preventBodyScroll]);

  // Toggle navigation
  const toggleNavigation = useCallback(() => {
    if (state.isOpen) {
      closeNavigation();
    } else {
      openNavigation();
    }
  }, [state.isOpen, openNavigation, closeNavigation]);

  // Handle touch start for swipe gestures
  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enableSwipeGestures || !state.isOpen) return;

      const touch = e.touches[0];
      setState((prev) => ({
        ...prev,
        touchStartX: touch.clientX,
        touchStartY: touch.clientY,
      }));
    },
    [enableSwipeGestures, state.isOpen],
  );

  // Handle touch end for swipe gestures
  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (
        !enableSwipeGestures ||
        !state.isOpen ||
        !state.touchStartX ||
        !state.touchStartY
      ) {
        return;
      }

      const touch = e.changedTouches[0];
      const diffX = touch.clientX - state.touchStartX;
      const diffY = touch.clientY - state.touchStartY;
      const absDiffX = Math.abs(diffX);
      const absDiffY = Math.abs(diffY);

      // Swipe right to close (for right-side navigation)
      if (absDiffX > absDiffY && diffX > 50 && absDiffX > 100) {
        closeNavigation();
      }
      // Swipe left to close (for left-side navigation)
      else if (absDiffX > absDiffY && diffX < -50 && absDiffX > 100) {
        closeNavigation();
      }

      setState((prev) => ({
        ...prev,
        touchStartX: null,
        touchStartY: null,
      }));
    },
    [
      enableSwipeGestures,
      state.isOpen,
      state.touchStartX,
      state.touchStartY,
      closeNavigation,
    ],
  );

  // Handle outside click
  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      if (!closeOnOutsideClick || !state.isOpen) return;

      const target = e.target as Element;
      const navigationContent = document.querySelector(
        "[data-navigation-content]",
      );
      const navigationTrigger = document.querySelector(
        "[data-navigation-trigger]",
      );

      if (
        navigationContent &&
        !navigationContent.contains(target) &&
        navigationTrigger &&
        !navigationTrigger.contains(target)
      ) {
        closeNavigation();
      }
    },
    [closeOnOutsideClick, state.isOpen, closeNavigation],
  );

  // Handle escape key
  const handleEscapeKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && state.isOpen) {
        closeNavigation();
      }
    },
    [state.isOpen, closeNavigation],
  );

  // Enhanced navigation item click handler
  const handleNavigationItemClick = useCallback(
    (callback?: () => void) => {
      // Add haptic feedback on supported devices
      if ("vibrate" in navigator) {
        navigator.vibrate(10);
      }

      // Close navigation after a short delay for better UX
      setTimeout(() => {
        closeNavigation();
      }, 150);

      // Execute callback if provided
      if (callback) {
        callback();
      }
    },
    [closeNavigation],
  );

  // Touch-friendly category item handler
  const handleCategoryItemPress = useCallback(
    (categorySlug?: string, onPress?: (slug?: string) => void) => {
      // Add visual feedback class
      const element = document.activeElement as HTMLElement;
      if (element) {
        element.classList.add("pressed");
        setTimeout(() => {
          element.classList.remove("pressed");
        }, 150);
      }

      // Haptic feedback
      if ("vibrate" in navigator) {
        navigator.vibrate(5);
      }

      // Execute press handler
      if (onPress) {
        onPress(categorySlug);
      }

      // Close navigation
      handleNavigationItemClick();
    },
    [handleNavigationItemClick],
  );

  // Setup event listeners
  useEffect(() => {
    if (state.isOpen) {
      document.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      document.addEventListener("touchend", handleTouchEnd, { passive: true });
      document.addEventListener("click", handleOutsideClick, true);
      document.addEventListener("keydown", handleEscapeKey);

      return () => {
        document.removeEventListener("touchstart", handleTouchStart);
        document.removeEventListener("touchend", handleTouchEnd);
        document.removeEventListener("click", handleOutsideClick, true);
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [
    state.isOpen,
    handleTouchStart,
    handleTouchEnd,
    handleOutsideClick,
    handleEscapeKey,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (preventBodyScroll) {
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("position");
        document.body.style.removeProperty("width");
      }
    };
  }, [preventBodyScroll]);

  // Check if device supports touch
  const isTouchDevice = useCallback(() => {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }, []);

  // Get optimal touch target size
  const getTouchTargetSize = useCallback(() => {
    const isTouch = isTouchDevice();
    return {
      minHeight: isTouch ? "48px" : "40px",
      minWidth: isTouch ? "48px" : "auto",
      padding: isTouch ? "12px 16px" : "8px 12px",
    };
  }, [isTouchDevice]);

  return {
    // State
    isOpen: state.isOpen,
    isAnimating: state.isAnimating,

    // Actions
    openNavigation,
    closeNavigation,
    toggleNavigation,
    handleNavigationItemClick,
    handleCategoryItemPress,

    // Utilities
    isTouchDevice,
    getTouchTargetSize,

    // Legacy support
    removeMobileNavigationStyles: closeNavigation,
  };
}

export default useMobileNavigation;
